import { AsyncPipe, NgIf } from '@angular/common';
import { Component, computed, input, output } from '@angular/core';
import { FlexModule } from '@angular/flex-layout';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDivider } from '@angular/material/divider';
import { MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatTooltip } from '@angular/material/tooltip';
import { MomentModule } from 'ngx-moment';
import { HireItem, Item, Product } from '../../models/index';
import { PriceSuggestion } from '../../models/product-hire-estimate';
import { AlertTextComponent } from '../alert-text/alert-text.component';
import { HlCurrencyPipe } from '../pipes/hl-currency.pipe';
import { PriceEstimateBreakdownPipe } from '../pipes/price-estimate-breakdown.pipe';
import { PricingTypeLabelComponent } from '../pricing-type-label/pricing-type-label.component';
import { ConfigService } from '../services/config.service';

@Component({
  selector: 'off-hire-item',
  template: `
    <div class="flex flex-col gap-3 w-full">
      <div class="flex flex-col md:flex-row gap-3 w-full">
        <div class="flex flex-col flex-1 gap-3">
          <div class="flex flex-col md:flex-row gap-3 w-full">
            <div class="flex flex-col gap-3 md:w-1/6">
              <p>{{ hireItem().quantity }} x {{ hireableTitle() }}</p>
              @if (hireableSubtitle()) {
                <p>
                  <small>{{ hireableSubtitle() }}</small>
                </p>
              }
            </div>
            <div class="flex flex-col gap-1 md:w-1/6">
              <p>On: {{ hireItem().on_hire | amDateFormat: dateTimeFormat }}</p>
              <p>
                <small>{{ hireItem().on_hire | amTimeAgo }}</small>
              </p>
            </div>
            <div class="flex flex-col gap-2 flex-1">
              <mat-form-field class="w-full">
                <input
                  type="number"
                  matInput
                  placeholder="Qty Returned"
                  required
                  [formControl]="quantityFormControl()"
                />
              </mat-form-field>
              <div class="flex flex-row items-center gap-2">
                <span class="font-semibold">Price:</span>
                @if (priceLoading()) {
                  <span>Loading...</span>
                } @else {
                  <span
                    *ngIf="pricingType() === 'fixed'"
                    [matTooltip]="
                      suggestedPriceResponse()
                        | priceEstimateBreakdown
                          : currency()
                          : currencyRate()
                          : hireItem().quantity
                    "
                    >{{
                      charge()
                        | hlCurrency: currency() : currencyRate() : false
                        | async
                    }}</span
                  >
                  <span
                    *ngIf="pricingType() !== 'fixed'"
                    [matTooltip]="
                      suggestedPriceResponse()
                        | priceEstimateBreakdown
                          : currency()
                          : currencyRate()
                          : hireItem().quantity
                    "
                    >{{
                      charge()
                        | hlCurrency
                          : currency()
                          : suggestedPriceCurrencyRate()
                          : false
                        | async
                    }}</span
                  ><app-pricing-type-label
                    [pricingType]="pricingType()"
                    (edit)="openPriceDialog.emit()"
                  ></app-pricing-type-label>
                }
              </div>
            </div>
          </div>
        </div>
        <div class="flex flex-col gap-4 w-full md:w-auto md:ml-4">
          <div class="flex flex-row items-center gap-2">
            <mat-slide-toggle
              color="accent"
              tabindex="-1"
              (change)="priceLaterToggled.emit($event.checked)"
            >
            </mat-slide-toggle>
            <p>Add price later</p>
          </div>
        </div>
      </div>
      @if (childItemNames()) {
        <app-alert-text type="warning">
          <p>
            This is a virtual product, the following items will be off hired as
            part of this action:
            {{ childItemNames() }}
          </p>
        </app-alert-text>
      }
      <div class="flex flex-row justify-center w-full">
        <mat-divider class="w-11/12"></mat-divider>
      </div>
    </div>
  `,
  standalone: true,
  imports: [
    FlexModule,
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    NgIf,
    MatSlideToggle,
    MatDivider,
    PriceEstimateBreakdownPipe,
    MomentModule,
    AlertTextComponent,
    MomentModule,
    HlCurrencyPipe,
    NgIf,
    MatTooltip,
    AsyncPipe,
    PricingTypeLabelComponent
  ]
})
export class OffHireItemComponent {
  hireItem = input.required<HireItem>();

  hireableTitle = computed(() => {
    return this.hireItem().hireable_type === 'item'
      ? (this.hireItem().hireable as Item).code
      : (this.hireItem().hireable as Product).name;
  });

  hireableSubtitle = computed(() => {
    return this.hireItem().hireable_type === 'item'
      ? (this.hireItem().hireable as Item).product.name
      : null;
  });

  childItemNames = computed(() => {
    return this.hireItem()
      .children.map((child) => (child.hireable as Item).product.name)
      .join(', ');
  });

  priceLoading = computed(() => {
    if (this.pricingType() === 'fixed' || this.pricingType() === 'no_charge') {
      return false;
    }
    return Boolean(!this.suggestedPriceResponse());
  });

  suggestedPriceResponse = input<PriceSuggestion>();
  charge = input.required<number>();
  priceLaterToggled = output<boolean>();
  currencyRate = input.required<number>();
  pricingType = input.required<string>();
  currency = input.required<string>();
  suggestedPriceError = computed(() => {
    return this.suggestedPriceResponse()?.error;
  });

  suggestedPriceCurrencyRate = computed(() => {
    return this.suggestedPriceResponse()?.estimate?.currency_code ===
      this.currency()
      ? 1
      : this.currencyRate();
  });

  dateTimeFormat = ConfigService.dates.longDateTimeFormat;

  openPriceDialog = output<void>();
  quantityFormControl = input.required<FormControl<number>>();

  constructor() {}
}
